import type { EntryValueViewT } from "@patient-watch/types";
import type { EntryValueRowT } from "./checkboxRuns";

export function isNumberAnswer(ev: EntryValueViewT): boolean {
    return ev.input_key === "number" || ev.input_key === "count";
}

export function entryValueUnit(ev: EntryValueViewT): string | undefined {
    const unit = ev.unit ?? ev.inputs?.unit;
    return unit?.trim() || undefined;
}

export function formatJoinedMeasurements(values: EntryValueViewT[]): string {
    return values
        .map((ev) => {
            const raw = ev.number_value ?? (ev.value == null || ev.value === "" ? null : Number(ev.value));
            if (raw == null || Number.isNaN(Number(raw))) return null;
            const unit = entryValueUnit(ev);
            return unit ? `${Number(raw)} ${unit}` : String(Number(raw));
        })
        .filter((part): part is string => Boolean(part))
        .join(" ");
}

export function joinedMeasurementLabel(values: EntryValueViewT[]): string {
    const pageLabel = values[0]?.page_label?.trim();
    if (pageLabel) return pageLabel;
    return values[0]?.input_label?.trim() || "";
}

/** A lone unit picker on its page, when another page already shows numbered measurements with units. */
export function isStandaloneUnitChoice(ev: EntryValueViewT, all: EntryValueViewT[]): boolean {
    if (ev.input_key !== "single-choice" && ev.input_key !== "dropdown") return false;
    const siblings = all.filter((value) => value.page_index === ev.page_index);
    if (siblings.length !== 1) return false;
    return all.some((value) => value.page_index !== ev.page_index && isNumberAnswer(value) && entryValueUnit(value));
}

export function withoutStandaloneUnitChoices(values: EntryValueViewT[]): EntryValueViewT[] {
    return values.filter((ev) => !isStandaloneUnitChoice(ev, values));
}

export type DisplayEntryValueRowT = EntryValueRowT | { kind: "measurements"; entryValues: EntryValueViewT[] };

/** Fold consecutive number answers on the same page into one measurement row. */
export function foldMeasurementRuns(rows: EntryValueRowT[]): DisplayEntryValueRowT[] {
    const out: DisplayEntryValueRowT[] = [];
    let run: EntryValueViewT[] = [];

    const flush = () => {
        if (run.length > 1) out.push({ kind: "measurements", entryValues: run });
        else if (run[0]) out.push({ kind: "single", entryValue: run[0] });
        run = [];
    };

    for (const row of rows) {
        if (row.kind === "single" && isNumberAnswer(row.entryValue)) {
            if (run[0] && run[0].page_index !== row.entryValue.page_index) flush();
            run.push(row.entryValue);
        } else {
            flush();
            out.push(row);
        }
    }
    flush();
    return out;
}
